import { Base, createApp, importWhenVisible } from '@studiometa/js-toolkit';
import { Action, Figure } from '@studiometa/ui';
import { isDev } from './config.js';

/**
 * @typedef {{
 *   $children: {
 *     Action: Action[],
 *     Figure: Figure[],
 *     Header: Array<Promise<import('./components/Header/index.js').default>>
 *     Menu: Array<Promise<import('./components/Menu.js').default>>
 *     Video: Array<Promise<import('./components/Video.js').default>>
 *     Accordion: Array<Promise<import('./components/Accordion.js').default>>
 *     Competences: Array<Promise<import('./components/Competences.js').default>>
 *     Slider: Array<Promise<import('./components/Slider.js').default>>
 *     Frame: Array<Promise<typeof import('@studiometa/ui').Frame>>
 *     ScrollReveal: Array<Promise<import('./components/ScrollReveal.js').default>>
 *   }
 * }} AppProps
 */

/**
 * App class.
 * @augments {Base<AppProps>}
 */
class App extends Base {
  /**
   * App config.
   * @type {import('@studiometa/js-toolkit').BaseConfig}
   */
  static config = {
    name: 'App',
    log: isDev(),
    components: {
      Action,
      Figure,
      // Not lazy-loaded with `importWhenVisible`: the header is visible on load
      // and the nav is hidden on mobile.
      Header: () => import('./components/Header/index.js'),
      Menu: () => import('./components/Menu.js'),
      // Not lazy-loaded either: the instances must be mounted — and therefore
      // observed — on load, otherwise the elements already in the viewport
      // would never be revealed.
      ScrollReveal: () => import('./components/ScrollReveal.js'),
      Slider: () => importWhenVisible(() => import('./components/Slider.js'), 'Slider'),
      Video: () => importWhenVisible(() => import('./components/Video.js'), 'Video'),
      Accordion: () => importWhenVisible(() => import('./components/Accordion.js'), 'Accordion'),
      Competences: () =>
        importWhenVisible(() => import('./components/Competences.js'), 'Competences'),
      // « Voir plus » de l'archive réalisations : `Frame` embarque lui-même ses
      // enfants (`FrameAnchor`, `FrameTarget`, `FrameLoader`), inutile de les
      // déclarer ici. Chargé à la volée pour ne pas embarquer morphdom partout.
      Frame: () =>
        importWhenVisible(
          () => import('@studiometa/ui/Frame/index.js').then((m) => m.Frame),
          'Frame',
        ),
    },
  };
}

export default createApp(App, {
  root: document.documentElement,
  // `createApp` reads `breakpoints`, not `screens`: with the wrong key the
  // breakpoints stay undefined and `withBreakpointObserver` throws on mount,
  // which takes the whole app down with it.
  breakpoints: {
    '2xs': '0rem',
    xs: '30rem',
    s: '40rem',
    m: '48rem',
    l: '64rem',
    xl: '80rem',
    '2xl': '96rem',
    '3xl': '120rem',
  },
});
